// iter v28 r286 — Tab "I miei dati · sempre tuoi" nel Profilo.
//
// USP killer di Pronto vs Gestim/Mediasi: l'account è dell'agente, non
// dell'agenzia. Questa tab mostra all'agente cosa si porta via se cambia
// gruppo/agenzia, con conteggi reali (immobili, review, lead, ecc.) e un
// bottone per esportare tutto in JSON.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, House, Star, UsersThree, Briefcase, FileText,
  Microphone, ChatCircleDots, Camera, Image as ImageIcon,
  DownloadSimple, Spinner, Lock, Heart,
} from "@phosphor-icons/react";
import { api, API, apiError } from "@/lib/api";
import Cookies from "js-cookie";

const ICONS = {
  properties: House,
  reviews: Star,
  leads_qv: UsersThree,
  leads_mandate: Briefcase,
  contacts: ChatCircleDots,
  sopralluoghi: Microphone,
  social_assets: ImageIcon,
  estimates: FileText,
  viewings: Briefcase,
  photos: Camera,
};

export default function PortabilityTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: r } = await api.get("/me/portability-report");
        if (alive) setData(r);
      } catch (e) {
        toast.error(apiError(e, "Impossibile caricare il riepilogo"));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const onExport = async () => {
    setDownloading(true);
    try {
      const token = Cookies.get("vista_jwt");
      const resp = await fetch(`${API}/me/portability-export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pronto-i-miei-dati-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Archivio scaricato");
    } catch (e) {
      toast.error("Download fallito: " + (e.message || ""));
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-8 flex items-center gap-2 text-[#9A968B] text-sm" data-testid="portability-loading">
        <Spinner size={16} className="animate-spin" /> Carico il riepilogo…
      </div>
    );
  }
  if (!data) return null;

  const total = (data.items || []).reduce((a, b) => a + (b.count || 0), 0);

  return (
    <div className="mt-8 space-y-6" data-testid="portability-section">
      {/* Hero — il messaggio politico */}
      <div className="rounded-2xl border-2 border-[#1C1C1A] bg-gradient-to-br from-[#F1F4ED] via-white to-[#FAF8F3] p-6 sm:p-7">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-11 h-11 rounded-full bg-[#1C1C1A] text-white flex items-center justify-center">
            <ShieldCheck size={22} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E]">
              Il tuo account · non quello dell'agenzia
            </div>
            <h2 className="font-editorial text-2xl sm:text-3xl text-[#1C1C1A] mt-1.5 leading-tight">
              Se cambi azienda, con Pronto non perdi nulla.
            </h2>
            <p className="text-[13.5px] text-[#3A3A36] leading-relaxed mt-3">
              Pronto è il software dell'<strong>agente</strong>, non dell'agenzia. Recensioni, immobili
              caricati da te, contatti, sopralluoghi, post — restano collegati al <em>tuo</em> user,
              non al gruppo. Cambi RE/MAX, Tecnocasa, Gabetti, ti metti in proprio? Apri Pronto, fai
              login, è tutto lì.
            </p>
          </div>
        </div>

        {/* Stats riassuntive */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Iscritto da" value={data.months_active ? `${data.months_active} mesi` : "—"} />
          <Stat label="Dati portabili" value={total} />
          <Stat label="Pagina pubblica" value={data.public_slug ? "Attiva" : "—"} />
          <Stat label="Account ID" value={data.user_id?.slice(-6) || "—"} mono />
        </div>
      </div>

      {/* Lista dettagliata */}
      <div className="rounded-2xl border border-[#E5E2D8] bg-white overflow-hidden" data-testid="portability-list">
        <div className="px-5 py-3 border-b border-[#EDEAE2] flex items-center gap-2">
          <Lock size={14} weight="duotone" className="text-[#7C8B6E]" />
          <div className="text-[11.5px] uppercase tracking-[0.16em] font-bold text-[#1C1C1A]">
            Cosa resta con te
          </div>
        </div>
        <ul className="divide-y divide-[#EDEAE2]">
          {(data.items || []).map((it) => {
            const Icon = ICONS[it.key] || FileText;
            return (
              <li key={it.key} className="px-5 py-3.5 flex items-start gap-3" data-testid={`portability-item-${it.key}`}>
                <div className="shrink-0 w-8 h-8 rounded-lg bg-[#F1F4ED] text-[#7C8B6E] flex items-center justify-center mt-0.5">
                  <Icon size={16} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="text-[13.5px] font-semibold text-[#1C1C1A] truncate">{it.label}</div>
                    <div className="text-[15px] font-editorial text-[#1C1C1A] tabular-nums">
                      {it.count}
                    </div>
                  </div>
                  {it.note && (
                    <div className="text-[11.5px] text-[#6B6A65] leading-snug mt-0.5">{it.note}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Esporta */}
      <div className="rounded-xl border border-[#E5E2D8] bg-[#FAF8F3] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1">
          <div className="font-semibold text-[14px] text-[#1C1C1A]">Esporta tutto in un archivio</div>
          <div className="text-[12px] text-[#6B6A65] leading-snug mt-0.5">
            Scarica un file JSON con il riepilogo dei tuoi immobili, recensioni, lead e contatti. Lo puoi tenere come backup o aprire con qualsiasi foglio di calcolo.
          </div>
        </div>
        <button
          onClick={onExport}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1C1C1A] hover:bg-black text-white text-[13px] font-semibold rounded-lg disabled:opacity-60 transition"
          data-testid="portability-export-btn"
        >
          {downloading ? (
            <><Spinner size={14} className="animate-spin" /> Preparo…</>
          ) : (
            <><DownloadSimple size={15} weight="bold" /> Scarica archivio</>
          )}
        </button>
      </div>

      <div className="text-center pt-2 pb-1">
        <Heart size={14} weight="fill" className="inline text-[#B58A4E] mb-1" />
        <div className="text-[11px] text-[#9A968B] leading-relaxed max-w-md mx-auto">
          Pronto è nato per gli agenti, non per le agenzie. Questo è un nostro impegno scritto:
          quello che hai costruito qui dentro è tuo, ovunque tu vada.
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div className="bg-white border border-[#E5E2D8] rounded-lg px-3 py-2.5">
      <div className="text-[9.5px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">{label}</div>
      <div className={`text-[16px] font-editorial text-[#1C1C1A] mt-0.5 ${mono ? "font-mono text-[13px]" : ""}`}>
        {value}
      </div>
    </div>
  );
}
